<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon kind="team" size="title" />
                <h2 class="title-subsection text-ink">Team Reflections</h2>
                <TabInfoToggle :expanded="infoOpen" topic="team reflections" @toggle="infoOpen = !infoOpen" />
            </div>
            <p class="body-small text-ink-muted">Give the 360 feedback your teammates have requested — your input shapes their reflection.</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <BaseDialog v-model:open="infoOpen" title="Give team feedback" title-style="serif">
            <HowTeamReflectionsWork />
        </BaseDialog>

        <div v-if="requests.length" class="flex flex-col gap-2">
            <div class="scaffold-details-small text-ink-muted">{{ dueLabel }}</div>
            <TeamRequestRow v-for="req in requests" :key="req.invite_token" :request="req" @give-feedback="emit('give-feedback', $event)" />
        </div>
        <ReflectionsEmptyState v-else kind="team" title="No feedback requests">
            No teammates have asked for your 360 feedback right now. We'll let you know the moment someone does.
        </ReflectionsEmptyState>
    </section>
</template>

<script setup lang="ts">
import type { TeamReflectionRequest } from "~vue/api/generated/reflections";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import HowTeamReflectionsWork from "~vue/pages/reflections/HowTeamReflectionsWork.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import ReflectionsEmptyState from "~vue/pages/reflections/ReflectionsEmptyState.vue";
import TabInfoToggle from "~vue/pages/reflections/TabInfoToggle.vue";
import TeamRequestRow from "~vue/pages/reflections/TeamRequestRow.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, ref } from "vue";

interface Props {
    requests: TeamReflectionRequest[];
}

const props = defineProps<Props>();
const emit = defineEmits<{ (event: "give-feedback", request: TeamReflectionRequest): void }>();

const infoOpen = ref(false);

const dueLabel = computed(() => {
    const withClose = props.requests.filter((r) => r.closes_at);
    if (!withClose.length) {
        return "Due";
    }
    const earliest = withClose.reduce((acc, r) => {
        if (!acc) {
            return r.closes_at as string;
        }
        return new Date(r.closes_at as string) < new Date(acc) ? (r.closes_at as string) : acc;
    }, withClose[0].closes_at as string);
    const days = Math.round((new Date(earliest).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days <= 0) {
        return "Due today";
    }
    if (days === 1) {
        return "Due tomorrow";
    }
    if (days <= 7) {
        return `Due in ${days} days`;
    }
    const short = formatDate(earliest, "short");
    return short ? `Due ${short}` : "Due";
});
</script>
